import React, { useContext, useState } from "react";

import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  KeyboardArrowDown as ChevronDownIcon,
  KeyboardArrowUp as ChevronUpIcon,
  Remove as RemoveIcon,
  ShoppingBasketOutlined as BasketIcon,
} from "@mui/icons-material";
import {
  Box,
  ButtonBase,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import { makeStyles } from "tss-react/mui";

import { colors } from "@/theme/overrides/colors";
import { mealPatternNameLabel } from "@/utils/food";
import { round } from "@/utils/number";

import MealPatternItem from "../store/MealPatternItem";
import MealPlanFormStore from "../store/MealPlanFormStore";
import { PlannedMealItem } from "../store/PlannedMealItem";

const useStyles = makeStyles()((theme) => ({
  root: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    // Sit above the search overlay so the cart is always reachable.
    zIndex: theme.zIndex.modal + 1,
    backgroundColor: theme.palette.common.white,
    borderTop: "1px solid #EDF0F5",
    boxShadow: "0px -4px 16px rgba(17, 35, 64, 0.08)",
    display: "flex",
    flexDirection: "column",
    maxHeight: "60vh",
  },
  header: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(3, 3),
  },
  headerSide: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  count: {
    fontWeight: 700,
    fontSize: 17,
    color: colors.dark,
  },
  kcal: {
    color: theme.palette.primary.main,
    fontWeight: 700,
    fontSize: 17,
  },
  list: {
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
  },
  itemRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 3),
  },
  itemName: {
    fontWeight: 600,
    color: colors.dark,
  },
  itemMeta: {
    color: colors.gray,
    fontSize: 13,
  },
  controls: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },
  empty: {
    padding: theme.spacing(3),
    textAlign: "center",
    color: colors.gray,
  },
}));

const itemName = (item: PlannedMealItem | MealPatternItem): string =>
  item instanceof MealPatternItem
    ? mealPatternNameLabel(item.name)
    : item.food.name;

const itemAmount = (item: PlannedMealItem | MealPatternItem): string =>
  item instanceof MealPatternItem
    ? item.amountWithUnit
    : `${round(item.amount, 2)} ${item.foodPortion.unitWithModifier}`;

/**
 * Mobile-only "cart" pinned to the bottom of the food-search overlay. It lets a
 * coach see (and tweak) the items already added to the active cell while they
 * keep searching, without closing the search. Collapsed by default so it barely
 * occupies space; tap the header to expand the list.
 */
const MobileCellCart: React.FC = observer(() => {
  const { classes } = useStyles();
  const [expanded, setExpanded] = useState(false);

  const store = useContext(MealPlanFormStore.Context);
  const cell = store.cells.active;
  if (!cell) return null;

  const items = cell.items as Array<PlannedMealItem | MealPatternItem>;

  return (
    <div className={classes.root}>
      <ButtonBase
        className={classes.header}
        onClick={() => setExpanded((value) => !value)}
      >
        <span className={classes.headerSide}>
          <BasketIcon htmlColor={colors.primary} />
          <Typography className={classes.count}>
            {items.length} {items.length === 1 ? "item" : "items"} in this cell
          </Typography>
        </span>
        <span className={classes.headerSide}>
          <Typography className={classes.kcal}>
            {Math.round(cell.totalEnergyKcal)} kcal
          </Typography>
          {expanded ? <ChevronDownIcon /> : <ChevronUpIcon />}
        </span>
      </ButtonBase>
      {expanded && (
        <>
          <Divider />
          <div className={classes.list}>
            {items.length === 0 ? (
              <Typography className={classes.empty}>
                No items yet. Add from the list above.
              </Typography>
            ) : (
              items.map((item, index) => (
                <React.Fragment key={item.uuid}>
                  {index > 0 && <Divider />}
                  <div className={classes.itemRow}>
                    <Box minWidth={0} flex={1}>
                      <Typography className={classes.itemName} noWrap>
                        {itemName(item)}
                      </Typography>
                      <Typography className={classes.itemMeta}>
                        {itemAmount(item)} &middot;{" "}
                        {Math.round(item.energyKcal)} kcal
                      </Typography>
                    </Box>
                    <div className={classes.controls}>
                      <IconButton
                        size="small"
                        aria-label="decrease amount"
                        onClick={() => item.decrementAmount()}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label="increase amount"
                        onClick={() => item.incrementAmount()}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label="remove item"
                        onClick={() => item.remove()}
                      >
                        <DeleteIcon fontSize="small" htmlColor="#FF6262" />
                      </IconButton>
                    </div>
                  </div>
                </React.Fragment>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
});

export default MobileCellCart;
